/**
 * 六神煞推演引擎（PLAN §6.1 —— 日历取数与笔画取数共用同一引擎）
 *
 * 算法（0 基指位）：
 *   手指 = [大安, 留连, 速喜, 赤口, 小吉, 空亡]           // 0..5
 *   fi = (month - 1) % 6          // 从大安上起月
 *   fj = (fi + (day - 1)) % 6     // 月上起日
 *   fk = (fj + (hour - 1)) % 6    // 日上起时
 *   结果 = 手指[fk]
 *
 * 纯函数、确定性，双端 + Worker 一致。
 */
import { getZhangJue } from './zhangjue';
const FINGERS = ['大安', '留连', '速喜', '赤口', '小吉', '空亡'];
/** 指位 0..5 → 名称 */
function finger(i) {
    return FINGERS[i] ?? '';
}
/**
 * 核心推演（取数已定）：
 * @param month 农历月 1..12
 * @param day 农历日 1..30（超出由调用方收紧）
 * @param hour 时辰序号 1..12（子=1 … 亥=12）
 */
export function divinate(month, day, hour) {
    if (!Number.isInteger(month) || month < 1 || month > 12) {
        throw new RangeError(`月取数越界：${month}（应为 1..12）`);
    }
    if (!Number.isInteger(day) || day < 1 || day > 30) {
        throw new RangeError(`日取数越界：${day}（应为 1..30）`);
    }
    if (!Number.isInteger(hour) || hour < 1 || hour > 12) {
        throw new RangeError(`时辰越界：${hour}（应为 1..12）`);
    }
    const fi = (month - 1) % 6; // 从大安上起月
    const fj = (fi + (day - 1)) % 6; // 月上起日
    const fk = (fj + (hour - 1)) % 6; // 日上起时
    const steps = [
        {
            step: '起月',
            fromName: finger(0),
            toName: finger(fi),
            toId: fi + 1,
            desc: `从${finger(0)}上起${displayMonth(month)}，落于${finger(fi)}。`,
        },
        {
            step: '起日',
            fromName: finger(fi),
            toName: finger(fj),
            toId: fj + 1,
            desc: `从${finger(fi)}上起初${displayDay(day)}，落于${finger(fj)}。`,
        },
        {
            step: '起时',
            fromName: finger(fj),
            toName: finger(fk),
            toId: fk + 1,
            desc: `从${finger(fj)}上起${displayHour(hour)}，落于${finger(fk)}。`,
        },
    ];
    return {
        month,
        day,
        hour,
        steps,
        final: getZhangJue(fk + 1),
    };
}
// ---------- 中文取数描述辅助 ----------
const MONTH_CN = ['', '正月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '冬月', '腊月'];
const DAY_CN_BASE = ['初', '十', '廿', '三'];
const NUM_CN = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
const HOUR_CN = [
    '子时', '丑时', '寅时', '卯时', '辰时', '巳时', '午时', '未时', '申时', '酉时', '戌时', '亥时',
];
/** 月 → 中文（1 基） */
export function displayMonth(month) {
    return MONTH_CN[month] ?? `${month}月`;
}
/** 日（1 基 1..30）→ 中文（初一…三十） */
export function displayDay(day) {
    if (day < 1 || day > 30)
        return `${day}日`;
    if (day <= 10)
        return `${DAY_CN_BASE[0]}${NUM_CN[day]}`; // 初一…初十
    if (day < 20)
        return `十${NUM_CN[day - 10]}`; // 十一…十九（十 = NUM_CN[10]）
    if (day === 20)
        return '二十';
    if (day < 30)
        return `${DAY_CN_BASE[2]}${NUM_CN[day - 20]}`; // 二十一…廿九
    return '三十';
}
/** 时辰序号（1 基）→ 中文 */
export function displayHour(hour) {
    return HOUR_CN[hour - 1] ?? `${hour}时`;
}
/** 时辰序号 1..12 与名称对照（供 UI 使用） */
export const HOUR_NAMES = HOUR_CN;
//# sourceMappingURL=engine.js.map